import { get, set, del } from "idb-keyval";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { Persister } from "@tanstack/react-query-persist-client";

const idbStorage = {
  getItem: async (key: string) => {
    const v = await get<string>(key);
    return v ?? null;
  },
  setItem: async (key: string, value: string) => {
    await set(key, value);
  },
  removeItem: async (key: string) => {
    await del(key);
  }
};

export function persistKeyForUser(userId: string | null | undefined): string {
  return userId ? `takacs-rq-cache:${userId}` : "takacs-rq-cache:anonymous";
}

/** Batch B: shorter throttle so warm prefetch flushes to IDB before a quick offline. */
export function createQueryPersister(userId: string | null | undefined): Persister {
  return createAsyncStoragePersister({
    storage: idbStorage,
    key: persistKeyForUser(userId),
    throttleTime: 400
  });
}

/** Wipe persisted React Query cache for a user (logout / user switch). */
export async function clearQueryPersist(userId?: string | null): Promise<void> {
  if (userId) {
    await del(persistKeyForUser(userId));
    return;
  }
  // null/undefined → wipe anonymous + every discovered takacs-rq-cache:* key
  const keys = [persistKeyForUser(null), ...(await listTakacsPersistKeys())];
  await Promise.all([...new Set(keys)].map((k) => del(k)));
}

async function listTakacsPersistKeys(): Promise<string[]> {
  try {
    const { keys } = await import("idb-keyval");
    const all = await keys();
    return all.map(String).filter((k) => k.startsWith("takacs-rq-cache"));
  } catch {
    return [];
  }
}

/**
 * Clear any leftover Workbox caches that might hold auth-scoped responses (legacy).
 * Do NOT reintroduce Supabase REST caching here — see src/sw.ts Batch A note.
 */
export async function clearLegacyApiCaches(): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((n) => /api-cache|supabase|workbox-.*runtime/i.test(n) || n === "api-cache")
        .map((n) => caches.delete(n))
    );
  } catch {
    /* ignore */
  }
}
